import { useCallback, useEffect, useRef, useState } from 'react';

export const useScrollOverflow = <T extends HTMLElement>() => {
    const ref = useRef<T>(null);

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const update = useCallback(() => {
        const element = ref.current;

        if (!element) {
            return;
        }

        const { scrollLeft, scrollWidth, clientWidth } = element;

        setCanScrollLeft(scrollLeft > 0);
        // 1px tolerance for fractional scroll positions on zoomed/high-DPI displays
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }, []);

    useEffect(() => {
        const element = ref.current;

        if (!element) {
            return;
        }

        update();

        element.addEventListener('scroll', update, { passive: true });

        const resizeObserver = new ResizeObserver(update);

        resizeObserver.observe(element);

        // Observing children catches content width changes (e.g. locale switch) that don't resize the container
        for (const child of element.children) {
            resizeObserver.observe(child);
        }

        return () => {
            element.removeEventListener('scroll', update);
            resizeObserver.disconnect();
        };
    }, [update]);

    return { ref, canScrollLeft, canScrollRight };
};
